/**
 * Anordnung der Ahnentafel — reine Rechnung, ohne React.
 *
 * Ein Stammbaum IST kein Baum: Personen haben zwei Elternlinien, dieselbe
 * Person steht in mehreren Familien, und in einer Dorfsippe heiraten Cousins
 * einander. Die API liefert deshalb einen Graphausschnitt, und hier entsteht
 * daraus ein lesbares Bild:
 *
 * 0. AUSWAHL. Der Graphausschnitt enthält alles im Umkreis — auch die Eltern
 *    der Schwägerin und die Cousins zweiten Grades. Alle in dieselbe Zeile
 *    geworfen ergibt das keinen Stammbaum, sondern eine Namensliste mit
 *    Linien. Deshalb wird zuerst ausgewählt, was gezeigt wird: Vorfahren,
 *    Nachkommen oder beides (Sanduhr).
 * 1. GENERATIONEN aus dem Familiengraph, nicht aus dem Verwandtschaftsgrad.
 *    Der Grad ist der KÜRZESTE Weg und kann quer durch eine Heirat laufen —
 *    dann stünden Eltern und Kind in derselben Zeile.
 * 2. PAARE gelten als eine Spalte und werden erst zum Schluss wieder in zwei
 *    Kästen aufgelöst, damit der Partnerbalken nicht über fremde Kästen läuft.
 * 3. BARYZENTRUM-Durchgänge rücken jede Spalte zu ihren Verwandten hin.
 * 4. ABSTÄNDE zwischen Familiengruppen und versetzte Verbindungsbalken,
 *    damit man sieht, wer zu wem gehört.
 */
import type { PersonKurz } from "../api";

export const BOX_B = 140;
export const BOX_H = 58;
const SPALTE = BOX_B + 22;
const ZEILE = BOX_H + 104;
/** Zusätzlicher Platz zwischen zwei Spalten, die keine Familie teilen. */
const GRUPPENABSTAND = 46;
/** Wie stark Partner und Geschwister ziehen, verglichen mit Eltern/Kindern. */
const GEWICHT_GLEICHE_ZEILE = 0.5;
const DURCHGAENGE = 6;
/** Versatz der Verbindungsbalken, damit sie nicht aufeinander liegen. */
const BALKEN_VERSATZ = 9;
const VERSATZ_STUFEN = 4;

export type Ansicht = "sanduhr" | "vorfahren" | "nachkommen" | "umgebung";

export const ANSICHT_LABEL: Record<Ansicht, string> = {
  sanduhr: "Vorfahren und Nachkommen",
  vorfahren: "nur Vorfahren",
  nachkommen: "nur Nachkommen",
  umgebung: "ganze Umgebung",
};

export interface BaumDaten {
  wurzel: number;
  personen: (PersonKurz & {
    entfernung?: { aufwaerts: number; abwaerts: number };
    portrait?: { medienId: number; tagId: number | null } | null;
  })[];
  familien: { id: number; bezeichnung: string | null }[];
  kanten: { familieId: number; personId: number; rolle: "elternteil" | "kind" }[];
}

export interface Knoten {
  id: number;
  person: BaumDaten["personen"][number];
  generation: number;
  x: number;
  y: number;
}

type Rolle = "elternteil" | "kind";

export function anordnen(daten: BaumDaten, modus: Ansicht = "sanduhr") {
  const personVon = new Map(daten.personen.map((p) => [p.id, p]));

  // --- Kanten einsammeln ---------------------------------------------------
  const familienVonPerson = new Map<number, number[]>();
  const mitgliederVonFamilie = new Map<number, { id: number; rolle: Rolle }[]>();

  const kantenEinlesen = (kanten: BaumDaten["kanten"], erlaubt?: Set<number>) => {
    familienVonPerson.clear();
    mitgliederVonFamilie.clear();
    for (const k of kanten) {
      if (!personVon.has(k.personId)) continue;
      if (erlaubt && !erlaubt.has(k.personId)) continue;
      let fp = familienVonPerson.get(k.personId);
      if (!fp) familienVonPerson.set(k.personId, (fp = []));
      fp.push(k.familieId);
      let mf = mitgliederVonFamilie.get(k.familieId);
      if (!mf) mitgliederVonFamilie.set(k.familieId, (mf = []));
      mf.push({ id: k.personId, rolle: k.rolle });
    }
  };
  kantenEinlesen(daten.kanten);

  const rolleIn = (personId: number, familieId: number) =>
    mitgliederVonFamilie.get(familieId)?.find((m) => m.id === personId)?.rolle;

  /** Familien, in denen die Person Kind ist (ihre Herkunftsfamilien). */
  const herkunft = (id: number) =>
    (familienVonPerson.get(id) ?? []).filter((f) => rolleIn(id, f) === "kind");
  /** Familien, in denen die Person Elternteil ist (ihre eigenen Familien). */
  const eigene = (id: number) =>
    (familienVonPerson.get(id) ?? []).filter((f) => rolleIn(id, f) === "elternteil");

  const mit = (familieId: number, rolle: Rolle) =>
    (mitgliederVonFamilie.get(familieId) ?? []).filter((m) => m.rolle === rolle).map((m) => m.id);

  const eltern = (id: number) => herkunft(id).flatMap((f) => mit(f, "elternteil"));
  const kinder = (id: number) => eigene(id).flatMap((f) => mit(f, "kind"));
  const geschwister = (id: number) => herkunft(id).flatMap((f) => mit(f, "kind")).filter((x) => x !== id);
  const partner = (id: number) => eigene(id).flatMap((f) => mit(f, "elternteil")).filter((x) => x !== id);

  // --- 0. Auswahl ----------------------------------------------------------
  const wurzel = personVon.has(daten.wurzel) ? daten.wurzel : daten.personen[0]?.id;

  function auswaehlen(): Set<number> | null {
    if (modus === "umgebung" || wurzel === undefined) return null;

    const menge = new Set<number>([wurzel]);
    const inRichtung = (start: number[], weiter: (id: number) => number[]) => {
      const schlange = [...start];
      while (schlange.length) {
        const hier = schlange.shift()!;
        for (const n of weiter(hier)) {
          if (menge.has(n)) continue;
          menge.add(n);
          schlange.push(n);
        }
      }
    };

    if (modus === "sanduhr" || modus === "vorfahren") inRichtung([wurzel], eltern);
    if (modus === "sanduhr" || modus === "nachkommen") inRichtung([wurzel], kinder);
    // Die eigenen Geschwister gehören dazu, die der Vorfahren nicht —
    // sonst hängt an jedem Urgroßvater dessen ganze Geschwisterschar.
    if (modus === "sanduhr") for (const g of geschwister(wurzel)) menge.add(g);

    // Partner: zu jedem Gezeigten der andere Elternteil, damit Paare
    // vollständig sind. Bei reinen Vorfahren sind das ohnehin schon beide.
    for (const id of [...menge]) for (const p of partner(id)) menge.add(p);

    return menge;
  }

  const auswahl = auswaehlen();
  if (auswahl) kantenEinlesen(daten.kanten, auswahl);

  const gezeigt = auswahl ?? new Set(daten.personen.map((p) => p.id));

  // --- 1. Generationen über den Familiengraph verbreiten --------------------
  const generationVon = new Map<number, number>();
  if (wurzel !== undefined) {
    generationVon.set(wurzel, 0);
    const schlange = [wurzel];
    while (schlange.length) {
      const hier = schlange.shift()!;
      const gen = generationVon.get(hier)!;
      for (const f of familienVonPerson.get(hier) ?? []) {
        const meine = rolleIn(hier, f);
        for (const m of mitgliederVonFamilie.get(f) ?? []) {
          if (generationVon.has(m.id)) continue;
          const ziel =
            meine === "kind"
              ? m.rolle === "elternteil"
                ? gen + 1
                : gen
              : m.rolle === "kind"
                ? gen - 1
                : gen;
          generationVon.set(m.id, ziel);
          schlange.push(m.id);
        }
      }
    }
  }
  for (const id of gezeigt) {
    if (generationVon.has(id)) continue;
    const e = personVon.get(id)?.entfernung;
    generationVon.set(id, e ? e.aufwaerts - e.abwaerts : 0);
  }

  // --- 2. Zeilen bilden, Paare zu Einheiten zusammenfassen -----------------
  const reihen = new Map<number, number[]>();
  for (const [id, gen] of generationVon) {
    let r = reihen.get(gen);
    if (!r) reihen.set(gen, (r = []));
    r.push(id);
  }

  /**
   * Geschwister stehen nach Geburtsdatum, das älteste links.
   *
   * Verglichen wird das ganze Datum, nicht nur das Jahr - zwei Brüder aus
   * demselben Jahr sollen nicht nach Laune nebeneinanderstehen. Wer kein
   * Datum hat, kommt dahinter; danach entscheidet der Vorname. Irgendeine
   * feste Ordnung braucht es, sonst sieht der Baum bei jedem Laden anders aus.
   */
  const nachGeburt = (a: number, b: number) => {
    const da = personVon.get(a)?.geburtVon ?? "";
    const db = personVon.get(b)?.geburtVon ?? "";
    if (da && db && da !== db) return da < db ? -1 : 1;
    if (da && !db) return -1;
    if (!da && db) return 1;
    return (personVon.get(a)?.vorname ?? "").localeCompare(personVon.get(b)?.vorname ?? "");
  };

  const kleinsteFamilie = (id: number) => Math.min(...(familienVonPerson.get(id) ?? [999999]));

  type Einheit = number[];
  const einheitenVon = new Map<number, Einheit[]>();

  for (const [gen, ids] of reihen) {
    ids.sort((a, b) => kleinsteFamilie(a) - kleinsteFamilie(b) || nachGeburt(a, b));

    const inZeile = new Set(ids);
    const vergeben = new Set<number>();
    const einheiten: Einheit[] = [];
    for (const id of ids) {
      if (vergeben.has(id)) continue;
      let mitPartner: number | undefined;
      for (const f of eigene(id)) {
        const andere = mit(f, "elternteil").filter(
          (x) => x !== id && inZeile.has(x) && !vergeben.has(x),
        );
        if (andere.length) {
          mitPartner = andere[0]!;
          break;
        }
      }
      vergeben.add(id);
      if (mitPartner !== undefined) {
        vergeben.add(mitPartner);
        einheiten.push([id, mitPartner]);
      } else {
        einheiten.push([id]);
      }
    }
    einheitenVon.set(gen, einheiten);
  }

  /** Familien einer Einheit — für den Gruppenabstand. */
  const familienDerEinheit = (e: Einheit) =>
    new Set(e.flatMap((id) => familienVonPerson.get(id) ?? []));

  const xVon = new Map<number, number>();
  const xNeuBerechnen = () => {
    for (const [, einheiten] of einheitenVon) {
      // Erst Breiten und Lücken bestimmen, dann um 0 zentrieren.
      const stellen: number[] = [];
      let lauf = 0;
      einheiten.forEach((e, i) => {
        if (i > 0) {
          const vorher = familienDerEinheit(einheiten[i - 1]!);
          const jetzt = familienDerEinheit(e);
          const gemeinsam = [...jetzt].some((f) => vorher.has(f));
          lauf += gemeinsam ? 0 : GRUPPENABSTAND;
        }
        for (const _ of e) {
          stellen.push(lauf);
          lauf += SPALTE;
        }
      });
      const gesamt = stellen.length ? stellen[stellen.length - 1]! : 0;
      const flach = einheiten.flat();
      flach.forEach((id, i) => xVon.set(id, stellen[i]! - gesamt / 2));
    }
  };
  xNeuBerechnen();

  // --- 3. Baryzentrum ------------------------------------------------------
  const nachbarn = (id: number) => {
    const raus: { id: number; gewicht: number }[] = [];
    const meineGen = generationVon.get(id)!;
    for (const f of familienVonPerson.get(id) ?? []) {
      for (const m of mitgliederVonFamilie.get(f) ?? []) {
        if (m.id === id) continue;
        raus.push({
          id: m.id,
          gewicht: generationVon.get(m.id) === meineGen ? GEWICHT_GLEICHE_ZEILE : 1,
        });
      }
    }
    return raus;
  };

  const schwerpunkt = (ids: number[], nur?: number) => {
    const eigeneIds = new Set(ids);
    const rechnen = nur !== undefined ? [nur] : ids;
    let summe = 0;
    let gewichte = 0;
    for (const id of rechnen) {
      for (const n of nachbarn(id)) {
        if (eigeneIds.has(n.id)) continue;
        summe += (xVon.get(n.id) ?? 0) * n.gewicht;
        gewichte += n.gewicht;
      }
    }
    if (gewichte === 0) {
      const eigeneX = rechnen.map((i) => xVon.get(i) ?? 0);
      return eigeneX.reduce((a, b) => a + b, 0) / eigeneX.length;
    }
    return summe / gewichte;
  };

  const generationen = [...einheitenVon.keys()].sort((a, b) => a - b);
  for (let runde = 0; runde < DURCHGAENGE; runde++) {
    const reihenfolge = runde % 2 === 0 ? generationen : [...generationen].reverse();
    for (const gen of reihenfolge) {
      const einheiten = einheitenVon.get(gen)!;
      if (einheiten.length < 2) continue;
      const ziel = new Map<Einheit, number>();
      for (const e of einheiten) ziel.set(e, schwerpunkt(e));
      einheiten.sort(
        (a, b) => ziel.get(a)! - ziel.get(b)! || (xVon.get(a[0]!) ?? 0) - (xVon.get(b[0]!) ?? 0),
      );
      xNeuBerechnen();
    }
  }

  /**
   * Geschwister in die Geburtsreihenfolge bringen - ohne das Bild neu zu
   * würfeln.
   *
   * Die Baryzentrum-Durchgänge ordnen nach Verwandtschaft: ein Bruder mit
   * eigener Familie weiter rechts wandert nach rechts, seine ledige Schwester
   * bleibt stehen. Für die Linienführung ist das richtig, zu lesen ist es
   * nicht - Geschwister stehen dann in zufälliger Reihenfolge nebeneinander.
   *
   * Deshalb werden hier nur die PLÄTZE neu vergeben, die die Kinder einer
   * Familie ohnehin schon belegen: die Spalten bleiben, wo die Linienführung
   * sie haben will, aber unter den Geschwistern sitzt das älteste links.
   * Steht ein Fremder zwischen zwei Geschwistern, bleibt er unangetastet.
   */
  const geschwisterOrdnen = () => {
    for (const [, einheiten] of einheitenVon) {
      if (einheiten.length < 2) continue;

      // Familie -> welche Plätze dieser Zeile ihre Kinder belegen.
      const plaetze = new Map<number, { platz: number; kind: number }[]>();
      einheiten.forEach((e, platz) => {
        for (const id of e) {
          for (const f of familienVonPerson.get(id) ?? []) {
            if (rolleIn(id, f) !== "kind") continue;
            let liste = plaetze.get(f);
            if (!liste) plaetze.set(f, (liste = []));
            // Ein Paar aus zwei Geschwistern (es gibt Vetternheiraten) darf
            // seinen Platz nicht zweimal beanspruchen.
            if (liste.some((x) => x.platz === platz)) continue;
            liste.push({ platz, kind: id });
          }
        }
      });

      // Feste Reihenfolge der Familien, damit dasselbe Bild herauskommt,
      // wenn eine Einheit zu zwei Familien gehört (beide Partner haben
      // Eltern in der Tafel).
      for (const familieId of [...plaetze.keys()].sort((a, b) => a - b)) {
        const gruppe = plaetze.get(familieId)!;
        if (gruppe.length < 2) continue;

        const belegt = gruppe.map((x) => x.platz).sort((a, b) => a - b);
        const einheitAn = new Map(gruppe.map((g) => [g.platz, einheiten[g.platz]!]));
        const sortiert = [...gruppe].sort((a, b) => nachGeburt(a.kind, b.kind));

        sortiert.forEach((eintrag, i) => {
          einheiten[belegt[i]!] = einheitAn.get(eintrag.platz)!;
        });
      }
    }
    xNeuBerechnen();
  };

  geschwisterOrdnen();

  // Innerhalb eines Paares: wer seine übrige Verwandtschaft weiter links
  // hat, steht links. BEIDE Partner werden dabei ausgeschlossen — zählt man
  // den anderen mit, gewinnt immer der, der gerade rechts steht.
  for (const [, einheiten] of einheitenVon) {
    for (const e of einheiten) {
      if (e.length !== 2) continue;
      const links = schwerpunkt(e, e[0]!);
      const rechts = schwerpunkt(e, e[1]!);
      if (links > rechts) e.reverse();
      else if (links === rechts) {
        const a = personVon.get(e[0]!)?.geschlecht;
        const b = personVon.get(e[1]!)?.geschlecht;
        if (a === "w" && b === "m") e.reverse();
      }
    }
  }
  xNeuBerechnen();

  // --- 4. Koordinaten ------------------------------------------------------
  const knoten: Knoten[] = [];
  for (const [gen, einheiten] of einheitenVon) {
    for (const id of einheiten.flat()) {
      knoten.push({
        id,
        person: personVon.get(id)!,
        generation: gen,
        x: xVon.get(id)!,
        y: -gen * ZEILE,
      });
    }
  }
  const knotenVon = new Map(knoten.map((k) => [k.id, k]));

  // --- 5. Familienverbindungen ---------------------------------------------
  // Alle Balken einer Generationsebene lägen sonst auf derselben Höhe und
  // damit übereinander. Deshalb bekommt jede Familie einen kleinen Versatz.
  const familienListe = [...mitgliederVonFamilie.keys()]
    .map((id) => {
      const knotenDazu = (mitgliederVonFamilie.get(id) ?? [])
        .map((m) => knotenVon.get(m.id))
        .filter(Boolean) as Knoten[];
      return { id, knotenDazu, linksX: Math.min(...knotenDazu.map((k) => k.x)) };
    })
    .filter((f) => f.knotenDazu.length >= 2)
    .sort((a, b) => a.linksX - b.linksX);

  const versatzVon = new Map<number, number>();
  const zaehlerJeEbene = new Map<number, number>();
  for (const f of familienListe) {
    const ebene = Math.max(...f.knotenDazu.map((k) => k.generation));
    const n = zaehlerJeEbene.get(ebene) ?? 0;
    zaehlerJeEbene.set(ebene, n + 1);
    versatzVon.set(f.id, (n % VERSATZ_STUFEN) * BALKEN_VERSATZ);
  }

  const verbindungen: { d: string; familieId: number }[] = [];

  for (const f of familienListe) {
    const mitglieder = mitgliederVonFamilie.get(f.id) ?? [];
    const elternK = mitglieder
      .filter((m) => m.rolle === "elternteil")
      .map((m) => knotenVon.get(m.id))
      .filter(Boolean) as Knoten[];
    const kinderK = mitglieder
      .filter((m) => m.rolle === "kind")
      .map((m) => knotenVon.get(m.id))
      .filter(Boolean) as Knoten[];

    const versatz = versatzVon.get(f.id) ?? 0;
    const teile: string[] = [];
    const elternUnten = elternK.length ? Math.max(...elternK.map((e) => e.y)) + BOX_H : null;
    const kinderOben = kinderK.length ? Math.min(...kinderK.map((k) => k.y)) : null;
    const genugPlatz = elternUnten === null || kinderOben === null || kinderOben - elternUnten >= 50;

    if (!genugPlatz) {
      // Eltern und Kinder in derselben oder verdrehter Zeile — kommt bei
      // Verwandtenheirat vor. Dann direkt verbinden statt Balken zeichnen.
      for (const e of elternK) {
        for (const k of kinderK) {
          teile.push(`M ${e.x} ${e.y + BOX_H / 2} L ${k.x} ${k.y + BOX_H / 2}`);
        }
      }
      verbindungen.push({ d: teile.join(" "), familieId: f.id });
      continue;
    }

    let abgangX: number | null = null;
    let abgangY: number | null = null;

    if (elternK.length > 0) {
      const balkenY = elternUnten! + 16 + versatz;
      const xs = elternK.map((e) => e.x);
      const links = Math.min(...xs);
      const rechts = Math.max(...xs);
      for (const e of elternK) teile.push(`M ${e.x} ${e.y + BOX_H} L ${e.x} ${balkenY}`);
      if (elternK.length > 1) teile.push(`M ${links} ${balkenY} L ${rechts} ${balkenY}`);
      abgangX = (links + rechts) / 2;
      abgangY = balkenY;
    }

    if (kinderK.length > 0) {
      const balkenY = kinderOben! - 20 - versatz;
      const xs = kinderK.map((k) => k.x);
      const links = Math.min(...xs);
      const rechts = Math.max(...xs);
      if (kinderK.length > 1) teile.push(`M ${links} ${balkenY} L ${rechts} ${balkenY}`);
      for (const k of kinderK) teile.push(`M ${k.x} ${balkenY} L ${k.x} ${k.y}`);

      if (abgangX !== null && abgangY !== null) {
        const anschlussX = Math.min(Math.max(abgangX, links), rechts);
        teile.push(`M ${abgangX} ${abgangY} L ${abgangX} ${balkenY}`);
        if (anschlussX !== abgangX) teile.push(`M ${abgangX} ${balkenY} L ${anschlussX} ${balkenY}`);
      }
    }

    if (teile.length) verbindungen.push({ d: teile.join(" "), familieId: f.id });
  }

  const alleX = knoten.map((k) => k.x);
  const alleY = knoten.map((k) => k.y);
  const rahmen = {
    x: Math.min(...alleX) - BOX_B / 2 - 40,
    y: Math.min(...alleY) - 40,
    breite: Math.max(...alleX) - Math.min(...alleX) + BOX_B + 80,
    hoehe: Math.max(...alleY) - Math.min(...alleY) + BOX_H + 80,
  };

  const wurzelKnoten = wurzel !== undefined ? knotenVon.get(wurzel) : undefined;

  return { knoten, verbindungen, rahmen, wurzelKnoten, gezeigt: knoten.length };
}
